import { Alert, Layouts, Loader } from 'components'
import s from './styles.module.scss'
import DashBoardNav from '../../dashboardNav'
import { useState, useEffect, useCallback, useRef } from 'react'
import { Link } from 'react-router-dom'
import * as api from 'api'
import { head, imageCompressor } from 'helpers'
import { LIMITS, clampField } from './limits'
import { LimitedInput, LimitedTextArea } from './fieldHelpers'

const IMAGE_HOST = process.env.REACT_APP_IMAGE_HOST

const EMPTY_CONTENT = {
  login: { desktop: {}, mobile: { stage1: {}, stage2: {} } },
  register: { desktop: {}, mobile: { stage1: {}, stage2: {} } }
}

const SECTIONS = [
  { id: 'desktop', label: 'Desktop', icon: 'desktop_windows', desc: 'Left/right welcome panel on big screens' },
  { id: 'mobile1', label: 'Mobile Slide 1', icon: 'phone_iphone', desc: 'First screen users see on phone (before form)' },
  { id: 'mobile2', label: 'Mobile Slide 2', icon: 'edit_note', desc: 'Form screen on phone (after Explore button)' }
]

const ICON_HINTS = 'groups, work, favorite, temple_hindu, event, verified_user, lock, store, forum, trending_up, location_city'

const resolvePreview = (path, pendingUrl) => {
  if (pendingUrl) return pendingUrl
  if (!path) return null
  if (path.startsWith('http') || path.startsWith('/')) return path
  return IMAGE_HOST + path
}

const Help = ({ children }) => <p className={s.help}>{children}</p>

const SectionHeader = ({ step, title, desc, where }) => (
  <div className={s.sectionHeader}>
    <span className={s.stepBadge}>Step {step}</span>
    <div>
      <h3 className={s.sectionTitle}>{title}</h3>
      <p className={s.sectionDesc}>{desc}</p>
      {where && <p className={s.whereShows}><span className='material-icons-outlined'>visibility</span> {where}</p>}
    </div>
  </div>
)

const FieldGroup = ({ title, children }) => (
  <div className={s.fieldGroup}>
    {title && <h4 className={s.groupTitle}>{title}</h4>}
    {children}
  </div>
)

export default function Main() {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [pageTab, setPageTab] = useState('login')
  const [section, setSection] = useState('desktop')
  const [content, setContent] = useState(EMPTY_CONTENT)
  const [pendingFiles, setPendingFiles] = useState({})
  const [pendingUrls, setPendingUrls] = useState({})
  const processing = useRef(false)

  const fetchContent = useCallback(async () => {
    if (processing.current) return
    processing.current = true
    setLoading(true)
    try {
      const res = await api.admin.authPageContent.fetch({})
      if (res?.code === 200) {
        setContent(res.payload?.content || EMPTY_CONTENT)
      } else {
        Alert.error(res?.message || 'Failed to load content')
      }
    } catch {
      Alert.error('Failed to load content')
    }
    setLoading(false)
    processing.current = false
  }, [])

  useEffect(() => {
    head({ title: 'Login & Register Pages | BCN' })
    fetchContent()
  }, [fetchContent])

  const updateField = (path, value) => {
    setContent(prev => {
      const next = JSON.parse(JSON.stringify(prev))
      const keys = path.split('.')
      let obj = next
      for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]]
      obj[keys[keys.length - 1]] = value
      return next
    })
  }

  const updateArrayItem = (path, index, field, value) => {
    setContent(prev => {
      const next = JSON.parse(JSON.stringify(prev))
      const keys = path.split('.')
      let obj = next
      for (let i = 0; i < keys.length; i++) obj = obj[keys[i]]
      obj[index][field] = value
      return next
    })
  }

  const handleImage = async (fieldKey, e) => {
    const file = e.target.files?.[0]
    if (!file) return
    try {
      const compressed = await imageCompressor([file], 'listing')
      const f = compressed[0]
      setPendingFiles(prev => ({ ...prev, [fieldKey]: f }))
      setPendingUrls(prev => ({ ...prev, [fieldKey]: URL.createObjectURL(f) }))
      Alert.success('Image selected. Click Save to upload.')
    } catch {
      Alert.error('Failed to process image')
    }
  }

  const saveHandler = async () => {
    if (processing.current) return
    processing.current = true
    setSaving(true)

    const data = new FormData()
    data.append('content', JSON.stringify(content))
    Object.entries(pendingFiles).forEach(([key, file]) => data.append(key, file))

    try {
      const res = await api.admin.authPageContent.update(data)
      if (res?.code === 201) {
        Alert.success('Saved! Changes will show on login/register pages.')
        setPendingFiles({})
        setPendingUrls({})
        await fetchContent()
      } else {
        Alert.error(res?.message || 'Save failed')
      }
    } catch {
      Alert.error('Save failed')
    }
    setSaving(false)
    processing.current = false
  }

  const page = content[pageTab] || {}
  const desktop = page.desktop || {}
  const stage1 = page.mobile?.stage1 || {}
  const stage2 = page.mobile?.stage2 || {}
  const isLogin = pageTab === 'login'
  const desktopSide = isLogin ? 'LEFT orange panel' : 'RIGHT cream/orange panel'

  const ImageUpload = ({ label, hint, fieldKey, currentPath }) => {
    const preview = resolvePreview(currentPath, pendingUrls[fieldKey])
    const inputId = `img-${fieldKey}`
    return (
      <div className={s.imageUpload}>
        <label className={s.imageLabel}>{label}</label>
        {hint && <Help>{hint}</Help>}
        <div className={s.imageRow}>
          <div className={s.previewBox}>
            {preview ? (
              <img src={preview} alt={label} className={s.preview} />
            ) : (
              <div className={s.previewEmpty}>
                <span className='material-icons-outlined'>image</span>
                <span>No image</span>
              </div>
            )}
          </div>
          <label htmlFor={inputId} className={s.uploadBtn}>
            <span className='material-icons-outlined'>upload</span>
            {preview ? 'Change Image' : 'Upload Image'}
          </label>
          <input id={inputId} type='file' accept='image/*' className={s.hiddenInput} onChange={e => handleImage(fieldKey, e)} />
        </div>
      </div>
    )
  }

  return (
    <div className={s.main}>
      <div className={s.outer}>
        <Layouts.Classic title='Login & Register Pages'>
          <div className={s.headerBottom}>
            <Link className={s.backLink} to={'../demographics'}>← Back to Demographics</Link>
            <button className={s.saveButton} onClick={saveHandler} disabled={saving}>
              <span className='material-icons-outlined'>save</span>
              {saving ? 'Saving...' : 'Save All Changes'}
            </button>
          </div>
        </Layouts.Classic>

        {!loading && (
          <div className={s.content + ' innerScrollX'}>
            <DashBoardNav />

            <div className={s.introBox}>
              <span className='material-icons-outlined'>info</span>
              <div>
                <strong>What is this?</strong>
                <p>Edit the welcome text and images shown on Login and Registration pages — on both <b>desktop</b> and <b>mobile</b>. Each field has a <b>character and line limit</b> so the layout on the live site stays clean. Press <b>Enter</b> for a new line where noted.</p>
                <ul className={s.limitsList}>
                  <li>Desktop heading: 2 lines, 40 chars each</li>
                  <li>Benefits: up to 6 lines, 55 chars each</li>
                  <li>Mobile hero title: up to 3 lines, 35 chars each</li>
                  <li>Images: use recommended sizes shown under each upload</li>
                </ul>
              </div>
            </div>

            {/* Page picker */}
            <div className={s.pageTabs}>
              <button className={pageTab === 'login' ? s.pageTabActive : ''} onClick={() => setPageTab('login')}>
                <span className='material-icons-outlined'>login</span>
                Login Page
              </button>
              <button className={pageTab === 'register' ? s.pageTabActive : ''} onClick={() => setPageTab('register')}>
                <span className='material-icons-outlined'>person_add</span>
                Registration Page
              </button>
            </div>

            {/* Section picker */}
            <div className={s.sectionTabs}>
              {SECTIONS.map(sec => (
                <button
                  key={sec.id}
                  className={section === sec.id ? s.sectionTabActive : ''}
                  onClick={() => setSection(sec.id)}
                >
                  <span className='material-icons-outlined'>{sec.icon}</span>
                  <span className={s.sectionTabLabel}>{sec.label}</span>
                  <span className={s.sectionTabDesc}>{sec.desc}</span>
                </button>
              ))}
            </div>

            {/* DESKTOP */}
            {section === 'desktop' && (
              <div className={s.section}>
                <SectionHeader
                  step={1}
                  title='Desktop Welcome Panel'
                  desc='The coloured side panel next to the login/register form on laptop and desktop.'
                  where={`Shows on the ${desktopSide} when screen is wide`}
                />

                <FieldGroup title='Main heading'>
                  <Help>Big title at the top of the welcome panel. Press Enter to break into two lines.</Help>
                  <LimitedTextArea
                    spec={LIMITS.desktop.heading}
                    label='Heading'
                    placeholder='e.g. Welcome Back,&#10;Community Member.'
                    value={desktop.heading || ''}
                    rows='3'
                    onChange={val => updateField(`${pageTab}.desktop.heading`, val)}
                  />
                </FieldGroup>

                <FieldGroup title='Benefits list (with tick marks)'>
                  <Help>Each line becomes one bullet point with a green checkmark.</Help>
                  <LimitedTextArea
                    spec={LIMITS.desktop.benefitsList}
                    label='Benefits — one per line'
                    placeholder={'Access Verified Local Businesses\nSecure Spiritual Connections\nExplore 150+ Verified Jobs'}
                    value={(desktop.benefits || []).join('\n')}
                    rows='6'
                    onChange={val => {
                      const lines = val.split('\n').slice(0, LIMITS.desktop.benefitsMax)
                        .map(l => clampField(l, LIMITS.desktop.benefitItem))
                      updateField(`${pageTab}.desktop.benefits`, lines)
                    }}
                  />
                </FieldGroup>

                <FieldGroup title='Three small image cards'>
                  <Help>Three photos shown in a row below the benefits. Add a short label under each.</Help>
                  {(desktop.illustrations || []).map((illus, i) => (
                    <div key={i} className={s.card}>
                      <div className={s.cardTitle}>Image card {i + 1}</div>
                      <LimitedInput
                        spec={LIMITS.desktop.illustrationLabel}
                        label='Caption under image'
                        placeholder='e.g. Trusted Families'
                        value={illus.label || ''}
                        onChange={val => updateArrayItem(`${pageTab}.desktop.illustrations`, i, 'label', val)}
                      />
                      <ImageUpload
                        label='Photo'
                        hint={LIMITS.images.desktopIllustration}
                        fieldKey={`${pageTab}DesktopIllus_${i}`}
                        currentPath={illus.image}
                      />
                    </div>
                  ))}
                </FieldGroup>

                <FieldGroup title='Community stats box (bottom of panel)'>
                  <Help>Small box at the bottom showing live community numbers.</Help>
                  <LimitedInput
                    spec={LIMITS.desktop.pulseLabel}
                    label='Small label above number'
                    placeholder='e.g. Live Community Pulse'
                    value={desktop.pulseLabel || ''}
                    onChange={val => updateField(`${pageTab}.desktop.pulseLabel`, val)}
                  />
                  <LimitedInput
                    spec={LIMITS.desktop.pulseCount}
                    label='Big number / headline'
                    placeholder='e.g. 478+ Verified Members'
                    value={desktop.pulseCount || ''}
                    onChange={val => updateField(`${pageTab}.desktop.pulseCount`, val)}
                  />
                  <LimitedInput
                    spec={LIMITS.desktop.pulseSub}
                    label='Small text below number'
                    placeholder='e.g. 26 local chapters actively growing across India'
                    value={desktop.pulseSub || ''}
                    onChange={val => updateField(`${pageTab}.desktop.pulseSub`, val)}
                  />
                </FieldGroup>
              </div>
            )}

            {/* MOBILE STAGE 1 */}
            {section === 'mobile1' && (
              <div className={s.section}>
                <SectionHeader
                  step={2}
                  title='Mobile — First Screen (Awareness)'
                  desc='What new users see first on phone before the login/register form.'
                  where='Full-screen orange slide users swipe through, then tap Explore'
                />

                <FieldGroup title='Explore button'>
                  <Help>Orange/white button at the bottom that opens the form.</Help>
                  <LimitedInput
                    spec={LIMITS.stage1.exploreButton}
                    label='Button text'
                    placeholder='Explore Your Community (2 min)'
                    value={stage1.exploreButton || ''}
                    onChange={val => updateField(`${pageTab}.mobile.stage1.exploreButton`, val)}
                  />
                </FieldGroup>

                <FieldGroup title='Trust badges (below button)'>
                  <Help>Three small icons with labels like "Verified Members". Icon = Google Material icon name.</Help>
                  <p className={s.iconHint}>Common icons: {ICON_HINTS}</p>
                  {(stage1.trustBadges || []).map((badge, i) => (
                    <div key={i} className={s.card}>
                      <div className={s.cardTitle}>Badge {i + 1}</div>
                      <LimitedInput spec={LIMITS.stage1.trustBadgeIcon} label='Icon name' placeholder='verified_user' value={badge.icon || ''} onChange={val => updateArrayItem(`${pageTab}.mobile.stage1.trustBadges`, i, 'icon', val)} />
                      <LimitedInput spec={LIMITS.stage1.trustBadgeLabel} label='Text below icon' placeholder='Verified Members' value={badge.label || ''} onChange={val => updateArrayItem(`${pageTab}.mobile.stage1.trustBadges`, i, 'label', val)} />
                    </div>
                  ))}
                </FieldGroup>

                <FieldGroup title='Swipeable slides (3 screens)'>
                  <Help>Users swipe left/right between 3 slides. Slide 1 is usually the main family/community photo.</Help>
                  {(stage1.slides || []).map((slide, i) => (
                    <div key={i} className={s.card}>
                      <div className={s.cardTitle}>
                        Slide {i + 1} {slide.featured ? '(Main — larger image)' : ''}
                      </div>
                      <LimitedInput spec={LIMITS.stage1.slideBadge} label='Small tag above title' placeholder='Trusted Network' value={slide.badge || ''} onChange={val => updateArrayItem(`${pageTab}.mobile.stage1.slides`, i, 'badge', val)} />
                      <LimitedTextArea spec={LIMITS.stage1.slideHeading} label='Big heading (Enter = new line)' placeholder='Welcome to Your&#10;Trusted Community' value={slide.heading || ''} rows='2' onChange={val => updateArrayItem(`${pageTab}.mobile.stage1.slides`, i, 'heading', val)} />
                      <LimitedInput spec={LIMITS.stage1.slideFeature} label='Bold phrase on image' placeholder='Connect & Grow:' value={slide.feature || ''} onChange={val => updateArrayItem(`${pageTab}.mobile.stage1.slides`, i, 'feature', val)} />
                      <LimitedTextArea spec={LIMITS.stage1.slideDescription} label='Text on image (after bold phrase)' placeholder='Forge lifelong connections with 1000s of members...' value={slide.description || ''} rows='2' onChange={val => updateArrayItem(`${pageTab}.mobile.stage1.slides`, i, 'description', val)} />
                      <LimitedTextArea spec={LIMITS.stage1.slideCta} label='Text below image' placeholder="Don't navigate life alone. Join BCN today." value={slide.cta || ''} rows='2' onChange={val => updateArrayItem(`${pageTab}.mobile.stage1.slides`, i, 'cta', val)} />
                      <ImageUpload
                        label='Slide photo'
                        hint={i === 0 ? LIMITS.images.mobileSlide : LIMITS.images.mobileSlide}
                        fieldKey={`${pageTab}MobileSlide_${i}`}
                        currentPath={slide.image}
                      />
                    </div>
                  ))}
                </FieldGroup>
              </div>
            )}

            {/* MOBILE STAGE 2 */}
            {section === 'mobile2' && (
              <div className={s.section}>
                <SectionHeader
                  step={3}
                  title='Mobile — Form Screen'
                  desc='Shown after user taps Explore — has the login/register form.'
                  where='Orange header + form fields + live feed on phone'
                />

                <FieldGroup title='Orange header area'>
                  <LimitedTextArea spec={LIMITS.stage2.heroTitle} label='Big title in orange header (Enter = new line)' placeholder='Welcome Back to&#10;Your Community' value={stage2.heroTitle || ''} rows='3' onChange={val => updateField(`${pageTab}.mobile.stage2.heroTitle`, val)} />
                  <LimitedInput spec={LIMITS.stage2.heroSub} label='Small subtitle (optional)' placeholder='Log in to access verified listings...' value={stage2.heroSub || ''} onChange={val => updateField(`${pageTab}.mobile.stage2.heroSub`, val)} />
                  <ImageUpload label='India map image' hint={LIMITS.images.map} fieldKey={`${pageTab}MobileMap`} currentPath={stage2.mapImage} />
                  <LimitedInput spec={LIMITS.stage2.mapTooltip} label='Tooltip on map' placeholder='Bengaluru: A new joiner this hour' value={stage2.mapTooltip || ''} onChange={val => updateField(`${pageTab}.mobile.stage2.mapTooltip`, val)} />
                </FieldGroup>

                <FieldGroup title='Two stat boxes in header'>
                  {(stage2.heroStats || []).map((st, i) => (
                    <div key={i} className={s.card}>
                      <div className={s.cardTitle}>Stat box {i + 1}</div>
                      <LimitedInput spec={LIMITS.stage2.heroStatIcon} label='Icon' placeholder='groups' value={st.icon || ''} onChange={val => updateArrayItem(`${pageTab}.mobile.stage2.heroStats`, i, 'icon', val)} />
                      <LimitedInput spec={LIMITS.stage2.heroStatValue} label='Number' placeholder='1000+' value={st.value || ''} onChange={val => updateArrayItem(`${pageTab}.mobile.stage2.heroStats`, i, 'value', val)} />
                      <LimitedInput spec={LIMITS.stage2.heroStatLabel} label='Label under number' placeholder='Active Members' value={st.label || ''} onChange={val => updateArrayItem(`${pageTab}.mobile.stage2.heroStats`, i, 'label', val)} />
                    </div>
                  ))}
                </FieldGroup>

                <FieldGroup title='Above the form fields'>
                  <LimitedInput spec={LIMITS.stage2.formTitle} label='Title above mobile/password fields' placeholder='Sign In to Your Account' value={stage2.formTitle || ''} onChange={val => updateField(`${pageTab}.mobile.stage2.formTitle`, val)} />
                </FieldGroup>

                <FieldGroup title='Live join feed (red dot section)'>
                  <Help>Shows fake/recent join activity to build trust. Appears below the form.</Help>
                  {(stage2.liveFeed || []).map((item, i) => (
                    <div key={i} className={s.card}>
                      <div className={s.cardTitle}>Feed row {i + 1}</div>
                      <div className={s.inlineFields}>
                        <LimitedInput spec={LIMITS.stage2.liveFeedCity} label='City' placeholder='Bengaluru' value={item.city || ''} onChange={val => updateArrayItem(`${pageTab}.mobile.stage2.liveFeed`, i, 'city', val)} />
                        <LimitedInput spec={LIMITS.stage2.liveFeedState} label='State code' placeholder='KA' value={item.state || ''} onChange={val => updateArrayItem(`${pageTab}.mobile.stage2.liveFeed`, i, 'state', val)} />
                        <LimitedInput spec={LIMITS.stage2.liveFeedTime} label='Time ago' placeholder='1m ago' value={item.time || ''} onChange={val => updateArrayItem(`${pageTab}.mobile.stage2.liveFeed`, i, 'time', val)} />
                      </div>
                    </div>
                  ))}
                </FieldGroup>

                <FieldGroup title='Footer link below form'>
                  <Help>e.g. "New here? Register Now" — prefix text + link text only (link goes to other page automatically).</Help>
                  <LimitedInput spec={LIMITS.stage2.footerPrefix} label='Text before link' placeholder='New here?' value={stage2.footerPrefix || ''} onChange={val => updateField(`${pageTab}.mobile.stage2.footerPrefix`, val)} />
                  <LimitedInput spec={LIMITS.stage2.footerLinkText} label='Link text (clickable)' placeholder='Register Now' value={stage2.footerLinkText || ''} onChange={val => updateField(`${pageTab}.mobile.stage2.footerLinkText`, val)} />
                </FieldGroup>
              </div>
            )}

            <div className={s.saveBar}>
              <p>Remember to save after editing each section.</p>
              <button className={s.saveButton} onClick={saveHandler} disabled={saving}>
                <span className='material-icons-outlined'>save</span>
                {saving ? 'Saving...' : 'Save All Changes'}
              </button>
            </div>
          </div>
        )}

        {loading && (
          <div className={s.loader}>
            <Loader message='Loading page content...' color='var(--c-primary)' colorText='var(--c-primary)' />
          </div>
        )}
      </div>
    </div>
  )
}
